import {
  environmentsStore,
  getGlobalVariableID,
  removeDuplicateEntry,
} from "@hoppscotch/common/newstore/environments"
import {
  getSettingSubject,
  settingsStore,
} from "@hoppscotch/common/newstore/settings"

import { getSyncInitFunction, type StoreSyncDefinitionOf } from "@app/lib/sync"

import * as E from "fp-ts/Either"

import { createMapper } from "@app/lib/sync/mapper"

import {
  clearGlobalEnvironmentVariables,
  createUserEnvironment,
  deleteUserEnvironment,
  updateUserEnvironment,
} from "./api"
import { SecretEnvironmentService } from "@hoppscotch/common/services/secret-environment.service"
import { getService } from "@hoppscotch/common/modules/dioc"
import { CurrentValueService } from "@hoppscotch/common/services/current-environment-value.service"

import { platform } from "@hoppscotch/common/platform"
import {
  recordEnvironmentImportLink,
  trackEnvironmentImport,
  waitForEnvironmentImports,
} from "@app/lib/environment-import-links"

export async function waitForPendingImports() {
  const userID = platform.auth.getCurrentUser()?.uid
  if (!userID) return
  await waitForEnvironmentImports(userID)
  if (platform.auth.getCurrentUser()?.uid !== userID)
    throw new Error("Account changed during environment import")
}

export const environmentsMapper = createMapper<number, string>()
export const globalEnvironmentMapper = createMapper<number, string>()

const secretEnvironmentService = getService(SecretEnvironmentService)
const currentEnvironmentValueService = getService(CurrentValueService)

export const storeSyncDefinition: StoreSyncDefinitionOf<
  typeof environmentsStore
> = {
  async createEnvironment({ name, variables }) {
    const lastCreatedEnvIndex = environmentsStore.value.environments.length - 1

    const res = await createUserEnvironment(name, JSON.stringify(variables))

    if (E.isRight(res)) {
      const id = res.right.createUserEnvironment.id

      secretEnvironmentService.updateSecretEnvironmentID(
        environmentsStore.value.environments[lastCreatedEnvIndex].id,
        id
      )

      currentEnvironmentValueService.updateEnvironmentID(
        environmentsStore.value.environments[lastCreatedEnvIndex].id,
        id
      )

      environmentsStore.value.environments[lastCreatedEnvIndex].id = id
      removeDuplicateEntry(id)
    }
  },
  async appendEnvironments({ envs }) {
    const userID = platform.auth.getCurrentUser()?.uid
    if (!userID) return
    const operation = Promise.all(
      envs.map(async (env) => {
        const sourceID = env.id
        // Mark this source unresolved before issuing the create, including reimports.
        await recordEnvironmentImportLink(userID, sourceID, null)
        const res = await createUserEnvironment(
          env.name,
          JSON.stringify(env.variables)
        )
        if (platform.auth.getCurrentUser()?.uid !== userID || E.isLeft(res))
          throw new Error("Environment import failed")
        const id = res.right.createUserEnvironment.id
        const local = environmentsStore.value.environments.find(
          (candidate) => candidate === env
        )
        if (local) {
          secretEnvironmentService.updateSecretEnvironmentID(sourceID, id)
          currentEnvironmentValueService.updateEnvironmentID(sourceID, id)
          local.id = id
          removeDuplicateEntry(id)
        }
        await recordEnvironmentImportLink(userID, sourceID, id)
      })
    )
    trackEnvironmentImport(userID, operation)
    try {
      await operation
    } catch {
      console.warn(
        "Environment import did not finish. Retry the environment import before importing linked collections."
      )
    }
  },
  async duplicateEnvironment({ envIndex }) {
    const environmentToDuplicate = environmentsStore.value.environments.find(
      (_, index) => index === envIndex
    )

    const lastCreatedEnvIndex = environmentsStore.value.environments.length - 1

    if (environmentToDuplicate) {
      const res = await createUserEnvironment(
        `${environmentToDuplicate?.name} - Duplicate`,
        JSON.stringify(environmentToDuplicate?.variables)
      )

      if (E.isRight(res)) {
        const id = res.right.createUserEnvironment.id
        environmentsStore.value.environments[lastCreatedEnvIndex].id = id

        removeDuplicateEntry(id)
      }
    }
  },
  updateEnvironment({ envIndex, updatedEnv }) {
    const backendId = environmentsStore.value.environments[envIndex].id
    if (backendId) {
      updateUserEnvironment(backendId, updatedEnv)()
    }
  },
  async deleteEnvironment({ envID }) {
    if (envID) {
      await deleteUserEnvironment(envID)()
    }
  },
  setGlobalVariables({ entries }) {
    const backendId = getGlobalVariableID()
    if (backendId) {
      updateUserEnvironment(backendId, {
        name: "",
        variables: entries.variables,
        id: "",
        v: 2,
      })()
    }
  },
  clearGlobalVariables() {
    const backendId = getGlobalVariableID()

    if (backendId) {
      clearGlobalEnvironmentVariables(backendId)
    }
  },
}

export const environnmentsSyncer = getSyncInitFunction(
  environmentsStore,
  storeSyncDefinition,
  () => settingsStore.value.syncEnvironments,
  getSettingSubject("syncEnvironments")
)
